<template>
  <div class="relative">
    <!-- 固定的封面背景 - 缩小高度 -->
    <div
      class="fixed top-0 left-0 right-0 h-[60vh] sm:h-[65vh] md:h-[70vh] z-0"
    >
      <!-- 轮播背景图片 -->
      <div
        v-for="(image, index) in headerImages"
        :key="index"
        class="absolute inset-0 bg-cover bg-center transition-opacity duration-1000"
        :class="{
          'opacity-0': currentImageIndex !== index,
          'opacity-100': currentImageIndex === index,
        }"
        :style="{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${image})`,
        }"
      ></div>

      <div class="absolute inset-0">
        <!-- 顶部导航栏 -->
        <nav class="relative z-20 px-4 sm:px-6 py-4 sm:py-5">
          <div class="flex items-center justify-between max-w-7xl mx-auto">
            <!-- Logo -->
            <router-link to="/" class="nav-logo">
              <span class="logo-icon">🪐</span>
              <span class="logo-text">hlly0x1</span>
              <div class="logo-underline"></div>
            </router-link>

            <!-- 导航按钮 -->
            <div class="flex items-center gap-2 sm:gap-3">
              <template v-for="item in navItems" :key="item.label">
                <!-- 普通按钮 -->
                <router-link
                  v-if="!item.dropdown"
                  :to="item.path"
                  class="nav-button"
                >
                  <span class="button-text">{{ item.label }}</span>
                  <div class="button-underline"></div>
                </router-link>

                <!-- 下拉菜单按钮 -->
                <div v-else class="relative group">
                  <button class="nav-button dropdown-button">
                    <span class="button-text">{{ item.label }}</span>
                    <svg
                      class="dropdown-arrow"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                    <div class="button-underline"></div>
                  </button>

                  <!-- 下拉菜单 -->
                  <div class="dropdown-menu">
                    <router-link
                      v-for="subItem in item.dropdown"
                      :key="subItem.label"
                      :to="subItem.path"
                      class="dropdown-item"
                    >
                      <span>{{ subItem.label }}</span>
                    </router-link>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </nav>

        <!-- 中央标题 -->
        <div
          class="absolute inset-0 flex flex-col items-center justify-center z-10 px-4"
        >
          <h1
            class="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white text-center mb-2 sm:mb-4 drop-shadow-2xl"
          >
            {{ title }}
          </h1>
          <p class="text-white text-sm sm:text-base md:text-lg opacity-90">
            {{ subtitle }}
          </p>
        </div>
      </div>
    </div>

    <!-- 内容区域（带波浪线） -->
    <div class="relative z-10" style="margin-top: 60vh">
      <!-- 波浪线容器 - 增大高度并向上延伸确保完全覆盖 -->
      <div
        class="absolute bottom-full left-0 right-0 h-[280px] sm:h-[320px] md:h-[360px] overflow-hidden"
      >
        <Wave />
      </div>

      <!-- 白色背景内容 -->
      <div class="bg-white min-h-screen">
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from "vue";
import Wave from "./wave.vue";
import { useNavigation } from "@/composables/useNavigation";

export default {
  name: "AppHeader",
  components: {
    Wave,
  },
  props: {
    title: {
      type: String,
      default: "Stay Simple, Stay Naive",
    },
    subtitle: {
      type: String,
      default: "这是坠吼的~",
    },
  },
  setup() {
    const { navItems } = useNavigation();

    // 动态加载所有头部图片
    const loadHeaderImages = () => {
      try {
        // 使用 require.context 动态导入 head 目录下的所有图片
        const context = require.context(
          "@/assets/pictures/head",
          false,
          /\.(png|jpe?g|gif|webp|svg)$/i
        );

        // 获取所有匹配的文件路径并转换为实际的图片 URL
        return context.keys().map((key) => context(key));
      } catch (error) {
        console.error("Error loading header images:", error);
        // 如果加载失败，返回空数组
        return [];
      }
    };

    const headerImages = loadHeaderImages();

    const currentImageIndex = ref(0);
    let intervalId = null;

    // 轮播逻辑
    const startCarousel = () => {
      intervalId = setInterval(() => {
        currentImageIndex.value =
          (currentImageIndex.value + 1) % headerImages.length;
      }, 10000); // 10秒切换一次
    };

    onMounted(() => {
      startCarousel();
    });

    onUnmounted(() => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    });

    return {
      navItems,
      headerImages,
      currentImageIndex,
    };
  },
};
</script>

<style scoped>
/* Logo 样式 */
.nav-logo {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  position: relative;
  padding: 0.5rem 0.75rem;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.nav-logo:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.logo-icon {
  font-size: 1.25rem;
  animation: rotate-planet 20s linear infinite;
}

@keyframes rotate-planet {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.logo-text {
  font-family: "Airstrip", sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  letter-spacing: 0.05em;
}

.logo-underline {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 2px;
  background: linear-gradient(to right, #3b82f6, #8b5cf6, #ec4899);
  transition: width 0.3s ease;
}

.nav-logo:hover .logo-underline {
  width: 80%;
}

/* 导航按钮样式 */
.nav-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  border-radius: 12px;
  color: white;
  font-size: 0.9375rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: visible;
}

.nav-button:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.button-text {
  position: relative;
  z-index: 1;
}

/* 按钮下划线效果 */
.button-underline {
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 2px;
  background: linear-gradient(to right, #3b82f6, #8b5cf6, #ec4899);
  transition: width 0.3s ease;
}

.nav-button:hover .button-underline {
  width: 80%;
}

/* 下拉箭头 */
.dropdown-arrow {
  width: 0.875rem;
  height: 0.875rem;
  transition: transform 0.3s ease;
}

.group:hover .dropdown-arrow {
  transform: rotate(180deg);
}

/* 下拉菜单 */
.dropdown-menu {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  min-width: 160px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  padding: 0.5rem;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 50;
}

.group:hover .dropdown-menu {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 0.625rem 0.875rem;
  color: #1f2937;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
}

.dropdown-item::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(to bottom, #3b82f6, #8b5cf6);
  transform: scaleY(0);
  transition: transform 0.2s ease;
}

.dropdown-item:hover {
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.1),
    rgba(139, 92, 246, 0.1)
  );
  color: #3b82f6;
  transform: translateX(4px);
}

.dropdown-item:hover::before {
  transform: scaleY(1);
}

/* 响应式 */
@media (max-width: 640px) {
  .nav-button {
    padding: 0.375rem 0.625rem;
    font-size: 0.8125rem;
  }

  .logo-text {
    font-size: 1.125rem;
  }

  .logo-icon {
    font-size: 1rem;
  }
}
</style>
